import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageSquare, FiX, FiSend, FiPhone, FiUser, FiCpu, FiMinus, FiCornerDownRight } = FiIcons;

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Initial State
  const [messages, setMessages] = useState([
    {
      type: 'bot',
      text: "Hi! Welcome to Ramirez Mobile Clean Truck. \n\nI can help with quotes, scheduling, or service questions. How can I assist you today?",
      options: ["Get a Quote", "Services", "Emergency Service", "Service Areas"],
      timestamp: new Date()
    }
  ]);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
    // Focus input when opened
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 300);
    }
  }, [messages, isOpen]);

  // Bot Logic / Knowledge Base
  const getBotResponse = (input) => {
    const lowerInput = input.toLowerCase();
    
    // Knowledge Base matching
    if (lowerInput.includes('emergency') || lowerInput.includes('urgent') || lowerInput.includes('asap')) {
      return {
        text: "For emergency service, please call us immediately. We offer mobile service to your location.",
        options: ["Call (909) 284-2738"]
      };
    }
    
    if (lowerInput.includes('quote') || lowerInput.includes('price') || lowerInput.includes('cost')) {
      return {
        text: "Our pricing is competitive:\n• Clean Truck Check: Starts at $150\n• Diagnostics: Starts at $120\n• Opacity Testing: Starts at $250\n\nFor a precise quote for your fleet, please give us a call.",
        options: ["Call for Quote", "See Services"]
      };
    }
    
    if (lowerInput.includes('service') || lowerInput.includes('what do you do')) {
      return {
        text: "We specialize in:\n• Clean Truck Checks (CARB)\n• Computer Diagnostics\n• Force Regens\n• Smoke Opacity Testing\n\nAll services are mobile - we come to you!",
        options: ["View All Services", "Book Appointment"]
      };
    }

    if (lowerInput.includes('area') || lowerInput.includes('location') || lowerInput.includes('where')) {
      return {
        text: "We serve San Bernardino County & surrounding areas including:\n• Ontario\n• Rancho Cucamonga\n• Fontana\n• Riverside\n• San Bernardino\n• Pomona",
        options: ["Check My City"]
      };
    }

    if (lowerInput.includes('clean truck check') || lowerInput.includes('carb')) {
      return {
        text: "We are fully certified for CARB Clean Truck Checks. We can perform the heavy-duty inspection and maintenance (HD I/M) testing required for compliance.",
        options: ["Get Compliant", "Pricing"]
      };
    }

    // Default response
    return {
      text: "I'd be happy to discuss that in detail. The best way to get specific answers for your truck is to speak with our dispatcher.",
      options: ["Call (909) 284-2738", "Service List"]
    };
  };

  const handleSendMessage = (text = inputText) => {
    if (!text.trim()) return;

    // Add User Message
    const userMessage = { type: 'user', text: text, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsTyping(true);

    // Simulate Bot Thinking
    setTimeout(() => {
      const response = getBotResponse(text);
      
      // Handle specific button actions that might redirect or call
      if (text === "Call (909) 284-2738" || text === "Call for Quote") {
        window.location.href = "tel:9092842738";
        setIsTyping(false);
        setMessages(prev => [...prev, { 
          type: 'bot', 
          text: "Calling now... Talk to you soon!", 
          timestamp: new Date() 
        }]);
        return;
      }

      const botMessage = {
        type: 'bot',
        text: response.text,
        options: response.options,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1200);
  };

  const handleOptionClick = (option) => {
    handleSendMessage(option);
  };

  return (
    <>
      {/* Chat Trigger Button */}
      <motion.button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 bg-blue-600 text-white p-4 rounded-full shadow-2xl hover:bg-blue-700 transition-all z-50 flex items-center justify-center border-2 border-white ${isOpen ? 'hidden' : 'flex'}`}
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
      >
        <SafeIcon icon={FiMessageSquare} className="text-2xl" />
        <span className="absolute -top-1 -right-1 bg-red-500 w-4 h-4 rounded-full border border-white"></span>
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-4 right-4 md:bottom-6 md:right-6 w-[90vw] md:w-96 h-[550px] max-h-[80vh] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border border-gray-100 font-sans"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-700 to-blue-600 text-white p-4 flex justify-between items-center shadow-md">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm relative">
                  <SafeIcon icon={FiCpu} className="text-xl text-white" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full border-2 border-blue-700"></div>
                </div>
                <div>
                  <h3 className="font-bold text-lg leading-tight">Ramirez Assistant</h3>
                  <p className="text-xs text-blue-100">Online | Replies instantly</p>
                </div>
              </div>
              <div className="flex items-center space-x-1">
                <button 
                  onClick={() => setIsOpen(false)} 
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  aria-label="Close chat"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              <div className="text-center text-xs text-gray-400 my-2">
                <span>{new Date().toLocaleDateString()}</span>
              </div>
              
              {messages.map((msg, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex flex-col ${msg.type === 'user' ? 'items-end' : 'items-start'}`}
                >
                  <div className={`flex items-end max-w-[85%] ${msg.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                    {/* Avatar */}
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mb-1 ${msg.type === 'user' ? 'bg-blue-100 ml-2' : 'bg-blue-600 mr-2'}`}>
                      <SafeIcon icon={msg.type === 'user' ? FiUser : FiCpu} className={`text-xs ${msg.type === 'user' ? 'text-blue-600' : 'text-white'}`} />
                    </div>

                    {/* Bubble */}
                    <div 
                      className={`p-3 rounded-2xl text-sm shadow-sm whitespace-pre-wrap ${
                        msg.type === 'user' 
                          ? 'bg-blue-600 text-white rounded-br-none' 
                          : 'bg-white text-gray-800 border border-gray-100 rounded-bl-none'
                      }`}
                    >
                      {msg.text}
                    </div>
                  </div>

                  {/* Quick Options Buttons */}
                  {msg.options && (
                    <div className="flex flex-wrap gap-2 mt-2 ml-10">
                      {msg.options.map((opt, optIdx) => (
                        <button
                          key={optIdx}
                          onClick={() => handleOptionClick(opt)}
                          className="text-xs bg-white text-blue-600 border border-blue-200 px-3 py-1.5 rounded-full hover:bg-blue-50 hover:border-blue-300 transition-colors shadow-sm font-medium"
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {/* Timestamp */}
                  <span className={`text-[10px] text-gray-400 mt-1 ${msg.type === 'user' ? 'mr-10' : 'ml-10'}`}>
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </motion.div>
              ))}

              {/* Typing Indicator */}
              {isTyping && (
                <div className="flex items-center space-x-2 ml-10">
                  <div className="flex space-x-1 bg-white p-3 rounded-2xl rounded-bl-none border border-gray-100 shadow-sm w-16 justify-center">
                    <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6 }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                    <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                    <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-100">
              <div className="flex items-center space-x-2 bg-gray-50 rounded-full px-4 py-2 border border-gray-200 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 bg-transparent border-none focus:ring-0 text-sm text-gray-700 placeholder-gray-400"
                />
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!inputText.trim()}
                  className={`p-2 rounded-full transition-all ${
                    inputText.trim() 
                      ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md' 
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <SafeIcon icon={FiSend} className="text-sm" />
                </button>
              </div>
              <div className="text-center mt-2">
                 <a href="tel:9092842738" className="text-xs text-gray-400 hover:text-blue-600 flex items-center justify-center gap-1 transition-colors">
                    <SafeIcon icon={FiPhone} className="text-[10px]" /> Call support directly
                 </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;